import { Pressable, StyleSheet, Text, View } from 'react-native';
import type { WorkoutEntryGroup } from '@jackedlogs/shared';

import { ChipStrip, IconButton } from '../index';
import { webTapFloor } from '../theme';
import { exerciseTrackLabel } from './exerciseTrackLabel';
import { type DraftEntry, rowIdOf } from './state/loggerDraft';
import type { useTheme } from './theme';

// RosterStrip (timeline logger redesign, slice 4 — "roster hybrid") — the
// horizontal exercise switcher above the FocusPanel. Replaces the bare name
// chips, which the v2.67/v2.68 smoke flagged as unclear ("la lista in coda è
// poco chiara … non si capisce quanti set si ha in coda"): each chip now reads
// at a glance as a tappable card showing the exercise's STATE (done /
// in-progress / not-done, never conflated — §7.3), its logged/planned set
// COUNT, and its superset GROUP marker. A section label makes the strip read as
// "tap to switch", not decoration.
//
// Read-only over the SETS (the locked roster-hybrid decision): ＋/− per set and
// the active set count live in the FocusPanel card (SetCountRow), not here.
// State is derived from the flat draft (completedAt per set) — no new field,
// data model untouched.
//
// PE-22 fetta E: the strip does own ONE action — "add an exercise", in the
// fixed leading slot. It belongs here because it is about the roster (what is
// in this workout), not about a set, and because this is the only place on the
// screen that is always visible. It replaces the button that used to sit
// between the note composer and the feed.

type RosterState = 'done' | 'inProgress' | 'notDone';

function rosterStateOf(entry: DraftEntry): RosterState {
  const planned = entry.sets.length;
  const logged = entry.sets.filter((s) => s.completedAt !== null).length;
  if (planned > 0 && logged >= planned) return 'done';
  if (logged > 0) return 'inProgress';
  return 'notDone';
}

export function RosterStrip({
  entries,
  activeEntryId,
  onFocus,
  resolveName,
  groupLabelFor,
  expanded,
  onToggleExpanded,
  onAddExercise,
  t,
  theme,
}: {
  entries: DraftEntry[];
  /** WHICH ROW is in focus — DraftEntry.entryId, never the catalog slug
   *  (LG-39). Keyed on the slug, a workout containing the same exercise
   *  twice lit every one of its chips at once and none of them could be
   *  told apart. */
  activeEntryId: string | null;
  onFocus: (entryId: string) => void;
  resolveName: (slug: string, fallback: string) => string;
  /** ctx.groupLabelFor — localizes a superset/circuit group to e.g. "1A".
   *  Accepts null (matches the ctx signature); only called with a non-null
   *  group below. */
  groupLabelFor: (group: WorkoutEntryGroup | null) => string;
  /** Expanded = chips wrap onto multiple lines (everything visible at a
   *  glance); collapsed = single horizontal scroll strip. The screen
   *  auto-expands at workout start (paired with the empty FocusPanel, a
   *  self-teaching first screen) and auto-collapses when an exercise is
   *  picked. The chevron toggles it manually any time. */
  expanded: boolean;
  onToggleExpanded: () => void;
  /** Opens the exercise picker. Lives in the strip's fixed slot (fetta E). */
  onAddExercise: () => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
  theme: ReturnType<typeof useTheme>;
}) {
  const styles = makeStyles(theme);

  // Prune completed exercises (Antenore 2026-06-23): the roster is "what's
  // left", so a done exercise drops off and lives in the timeline feed —
  // tap its minicard there to re-focus it.
  const remaining = entries.filter((e) => rosterStateOf(e) !== 'done');
  // ⛔ The strip used to return null here, and with it went everything it
  // carried. That already cost the gym chip once — the chip rode this header
  // and the place became unreachable exactly at "all logged" — and the cure
  // then was to move the chip away (it lives on the session name row now).
  // The ＋ can't take that cure: the fixed slot only means anything if it
  // survives the empty strip, so this time the STRIP stops disappearing.
  // With no chips left it shows its label and the ＋, and the FocusPanel's
  // "all logged" card below carries the words.

  const rows = remaining.map((entry) => {
    // The row's identity, used for all three of: which chip is lit, the React
    // key, and what a tap focuses. ⛔ Never entry.exerciseId — the same exercise
    // can hold several rows, and keying on the slug gave React duplicate keys
    // AND lit every one of them (LG-39).
    const rowId = rowIdOf(entry);
    const state = rosterStateOf(entry);
    const planned = entry.sets.length;
    const logged = entry.sets.filter((s) => s.completedAt !== null).length;
    const groupTag = entry.group ? groupLabelFor(entry.group) : null;
    const stateWord = t(`mobile.timeline.rosterState.${state}`, {
      defaultValue:
        state === 'done'
          ? 'done'
          : state === 'inProgress'
            ? 'in progress'
            : 'not started',
    });
    // The SPOKEN name folds in the machine (EQ-1: same exercise, two machines,
    // one session) — the real, athlete-recognizable thing that tells two rows
    // apart when it's set (exerciseTrackLabel, its 2nd real callsite). The
    // visual chip keeps the plain name below.
    const baseA11y = `${exerciseTrackLabel(
      resolveName(entry.exerciseId, entry.exerciseName),
      entry.equipmentInstance ?? undefined,
      t,
    )}, ${logged}/${planned}, ${stateWord}`;
    return { entry, rowId, state, planned, logged, groupTag, baseA11y };
  });

  // DS-43: three "not started" rows of one exercise spoke the identical string,
  // so a screen reader couldn't tell the chips apart. Where even the machine-
  // qualified reading collides (the "walk warm-up / HIIT / cool-down" case: no
  // machine, no group — the rows are not variants, loggerDraftTypes §entryId),
  // position is the only thing left, so the reading says which occurrence it is.
  // ⛔ It reflects the order the roster already renders, it does NOT decide a
  // reading order — that's LG-45, still open.
  const readingTotals = new Map<string, number>();
  for (const r of rows)
    readingTotals.set(r.baseA11y, (readingTotals.get(r.baseA11y) ?? 0) + 1);
  const readingSeen = new Map<string, number>();
  const a11yLabels = rows.map((r) => {
    const total = readingTotals.get(r.baseA11y) ?? 1;
    if (total <= 1) return r.baseA11y;
    const occurrence = (readingSeen.get(r.baseA11y) ?? 0) + 1;
    readingSeen.set(r.baseA11y, occurrence);
    return t('mobile.timeline.rosterOccurrenceA11y', {
      label: r.baseA11y,
      index: occurrence,
      count: total,
      defaultValue: '{{label}} ({{index}} of {{count}})',
    });
  });

  const chips = rows.map((r, i) => {
    const { entry, rowId, state, planned, logged, groupTag } = r;
    const active = rowId === activeEntryId;
    return (
      <Pressable
        key={rowId}
        accessibilityRole="button"
        accessibilityState={{ selected: active }}
        accessibilityLabel={a11yLabels[i]}
        onPress={() => onFocus(rowId)}
        hitSlop={8}
        style={({ pressed }) => [
          styles.chip,
          active && styles.chipActive,
          pressed && styles.chipPressed,
        ]}
      >
        <View style={[styles.dot, styles[`dot_${state}`]]} />
        <Text
          style={[styles.name, active && styles.nameActive]}
          numberOfLines={1}
        >
          {resolveName(entry.exerciseId, entry.exerciseName)}
        </Text>
        <Text style={[styles.count, styles[`count_${state}`]]}>
          {logged}/{planned}
          {state === 'done' ? ' ✓' : ''}
        </Text>
        {groupTag ? (
          <Text style={styles.groupTag} numberOfLines={1}>
            ⟨{groupTag}⟩
          </Text>
        ) : null}
      </Pressable>
    );
  });

  // With nothing left to switch between, "tap to switch" is an instruction for
  // a gesture that no longer exists and the chevron expands nothing — so both
  // go, and the ＋ is what the row is for.
  const hasChips = chips.length > 0;

  return (
    <ChipStrip
      label={t('mobile.timeline.rosterLabel', { defaultValue: 'Exercises' })}
      hint={
        hasChips
          ? t('mobile.timeline.rosterHint', { defaultValue: 'tap to switch' })
          : undefined
      }
      expanded={expanded}
      onToggleExpanded={onToggleExpanded}
      collapsible={hasChips}
      expandA11y={t('mobile.timeline.rosterExpandA11y', {
        defaultValue: 'Expand exercise list',
      })}
      collapseA11y={t('mobile.timeline.rosterCollapseA11y', {
        defaultValue: 'Collapse exercise list',
      })}
      leadingSlot={
        <IconButton
          theme={theme}
          name="plus"
          onPress={onAddExercise}
          // Outer edges full, shared inner edge given up — the idiom
          // SupersetTabs and CuePill already use for a tight row. The chips
          // carry a hitSlop of 8 too, and ChipStrip's 8pt gap is what the two
          // zones meet on. 36 + 8 on the left still clears the 44pt floor.
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 0 }}
          accessibilityLabel={t('mobile.logger.addExercise', {
            defaultValue: 'Add exercise',
          })}
        />
      }
      theme={theme}
    >
      {chips}
    </ChipStrip>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // Chrome (label/hint/chevron/grid/scroll) now lives in ChipStrip; these
    // are the logger's rich chip styles passed in as children.
    // Compact single-line chip (Antenore 2026-06-01: "una riga sola, chip
    // piccoli"): dot + name + count on one row, ≥44pt tall for the tap target
    // (reduced vision) while taking far less vertical space than the old
    // two-line card.
    chip: {
      maxWidth: 220,
      // 36 with a hitSlop of 8 reaches 44 on native. RNW does not implement
      // hitSlop at all, so on web the floor has to be the box: the chip read
      // as 36 until the ＋ landed beside it carrying webTapFloor and made the
      // difference visible (a11y audit, fetta E).
      minHeight: webTapFloor ?? 36,
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s1,
      backgroundColor: p.surface,
    },
    chipActive: {
      borderColor: p.brand500,
      borderWidth: 2,
      backgroundColor: p.brand100,
    },
    chipPressed: { backgroundColor: p.surface2 },
    dot: { width: 10, height: 10, borderRadius: 5 },
    // Shape-coded, not colour-only ([[feedback_user_visual_accessibility]]):
    // done/in-progress are FILLED discs (differ by colour + the count's
    // ✓/number), not-started is a hollow RING — readable without colour.
    dot_done: { backgroundColor: p.complete },
    dot_inProgress: { backgroundColor: p.brand500 },
    dot_notDone: {
      backgroundColor: 'transparent',
      borderWidth: 2,
      borderColor: p.borderStrong,
    },
    name: {
      flexShrink: 1,
      color: p.ink,
      fontSize: ty.sizes.sm,
      fontWeight: '600',
    },
    nameActive: { color: p.brandText },
    // DS-28: sits between the flexShrink:1 name and the fixed-width chip
    // (maxWidth 220) — without its own flexShrink:0 the row squeezes this
    // cell along with the name when the chip runs out of room, clipping
    // the set count mid-string ("Plank 0/" instead of "Plank 0/3").
    // minWidth reserves space for the widest count shape ("99/99 ✓").
    count: {
      flexShrink: 0,
      minWidth: 40,
      fontSize: ty.sizes.xs,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
    },
    count_done: { color: p.complete },
    count_inProgress: { color: p.brandText },
    count_notDone: { color: p.ink3 },
    groupTag: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: '600',
    },
  });
}
